import React from 'react';
import Icon from '../../../components/AppIcon';
import ProgressRing from './ProgressRing';

const PlanHeader = ({ 
  plan, 
  user, 
  progressPercentage = 0,
  className = ''
}) => {
  const getOccupationIcon = (occupation) => {
    const icons = {
      'Software Developer': 'Code',
      'Marketing Manager': 'Megaphone',
      'Sales Representative': 'TrendingUp',
      'Project Manager': 'Briefcase',
      'Data Analyst': 'BarChart3',
      'Designer': 'Palette',
      'Teacher': 'GraduationCap',
      'Nurse': 'Heart',
      'Engineer': 'Wrench',
      'Consultant': 'Users'
    };
    return icons?.[occupation] || 'Briefcase';
  };

  const getSeniorityBadge = (seniority) => {
    const badges = {
      'Apprentice': { color: 'bg-secondary/10 text-secondary', icon: 'BookOpen' },
      'Junior': { color: 'bg-primary/10 text-primary', icon: 'User' },
      'Mid': { color: 'bg-accent/10 text-accent', icon: 'Users' },
      'Senior': { color: 'bg-warning/10 text-warning', icon: 'Award' },
      'Executive': { color: 'bg-error/10 text-error', icon: 'Crown' }
    };
    return badges?.[seniority] || badges?.['Mid'];
  };

  if (!plan) {
    return (
      <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-muted rounded-lg"></div>
            <div className="flex-1 space-y-2">
              <div className="h-6 bg-muted rounded w-1/3"></div>
              <div className="h-4 bg-muted rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const seniorityBadge = getSeniorityBadge(plan?.seniority);

  return (
    <div className={`bg-card border border-border rounded-lg shadow-sm ${className}`}>
      <div className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-6 lg:space-y-0">
          {/* Plan Info */}
          <div className="flex-1 space-y-4">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon 
                  name={getOccupationIcon(plan?.occupation)} 
                  size={24} 
                  className="text-primary" 
                />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  Career Development Plan
                </h1>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg font-semibold text-foreground">
                      {plan?.occupation}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${seniorityBadge?.color} flex items-center space-x-1`}>
                      <Icon name={seniorityBadge?.icon} size={14} />
                      <span>{plan?.seniority}</span>
                    </span>
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Icon name="Calendar" size={14} />
                    <span>Created {new Date(plan.createdAt)?.toLocaleDateString()}</span>
                  </div>
                  {plan?.lastUpdated && (
                    <div className="flex items-center space-x-1">
                      <Icon name="RefreshCw" size={14} />
                      <span>Updated {new Date(plan.lastUpdated)?.toLocaleDateString()}</span>
                    </div>
                  )}
                  {user?.isPremium && (
                    <div className="flex items-center space-x-1">
                      <Icon name="Crown" size={14} className="text-warning" />
                      <span className="text-warning">Premium Plan</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Plan Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <div className="text-3xl font-bold text-primary">
                  {plan?.sections?.length || 6}
                </div>
                <div className="text-sm font-medium text-primary/80 mt-1">Sections</div>
              </div>
              <div className="text-center p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-950/20 dark:border-blue-800/30">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {plan?.totalExercises || 12}
                </div>
                <div className="text-sm font-medium text-blue-600/80 dark:text-blue-400/80 mt-1">Exercises</div>
              </div>
              <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg dark:bg-green-950/20 dark:border-green-800/30">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {plan?.estimatedWeeks || 8}
                </div>
                <div className="text-sm font-medium text-green-600/80 dark:text-green-400/80 mt-1">Weeks</div>
              </div>
              <div className="text-center p-4 bg-orange-50 border border-orange-200 rounded-lg dark:bg-orange-950/20 dark:border-orange-800/30">
                <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {plan?.kpis?.length || 5}
                </div>
                <div className="text-sm font-medium text-orange-600/80 dark:text-orange-400/80 mt-1">KPIs</div>
              </div>
            </div>
          </div>

          {/* Progress Ring */}
          <div className="flex-shrink-0 flex justify-center lg:justify-end">
            <ProgressRing 
              percentage={progressPercentage}
              size={140}
              strokeWidth={10}
              showLabel={true}
            />
          </div>
        </div>
      </div>
      {/* Status Bar */}
      <div className="px-6 py-3 bg-muted/30 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full"></div>
              <span className="text-muted-foreground">Plan Active</span>
            </div>
            {plan?.isSaved && (
              <div className="flex items-center space-x-2">
                <Icon name="Save" size={14} className="text-success" />
                <span className="text-muted-foreground">Saved</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Clock" size={14} />
            <span>Est. {plan?.estimatedHoursPerWeek || 3-5} hours/week</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanHeader;